import React, { useEffect, useState } from "react";
import {
    collection, addDoc, getDocs, updateDoc,
    deleteDoc, doc, query, orderBy, serverTimestamp
} from "firebase/firestore";
import { db } from "../../firebase";
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, TablePagination, InputAdornment, Grid
} from "@mui/material";
import { Plus, Search, Edit3, Trash2 } from "lucide-react";
import Swal from "sweetalert2";

function Insurancecustomers() {

    const [data, setData] = useState([]);
    const [plans, setPlans] = useState([]);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        id: null,

        insuranceId: " ",
        category: "",
        planName: "",
        premiumAmount: "",
        coverageAmount: "",
        duration: "",

        customerName: "",
        mobile: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        aadharNo: "",
        panNo: "",


        status: "Active"
    });

    // 🔹 FETCH DATA
    const fetchData = async () => {
        const q = query(collection(db, "insurance_customers"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        setData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchPlans = async () => {
        const snap = await getDocs(collection(db, "insurances"));
        setPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    useEffect(() => {
        fetchData();
        fetchPlans();
    }, []);

    // 🔹 VALIDATION
    const validate = () => {
        if (!formData.insuranceId) return "Select insurance plan";
        return null;
    };

    // 🔹 SUBMIT
    const handleSubmit = async () => {
        const error = validate();
        if (error) return Swal.fire("Error", error, "error");

        if (!formData.customerName?.trim()) {
            return showError("Customer Name required");
        }

        if (!formData.mobile || formData.mobile.length !== 10) {
            return showError("Valid 10 digit mobile required");
        }

        if (!formData.premiumAmount) {
            return showError("Premium Amount required");
        }

        if (!formData.aadharNo || formData.aadharNo.length !== 12) {
            return showError("Valid 12 digit Aadhar required");
        }

        if (!formData.panNo) {
            return showError("PAN number required");
        }

        if (!/^[0-9]{10}$/.test(formData.mobile)) {
            return showError("Enter valid 10 digit mobile");
        }

        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNo)) {
            return showError("Invalid PAN format (ABCDE1234F)");
        }


        try {
            const payload = {
                insuranceId: formData.insuranceId,
                category: formData.category,
                planName: formData.planName,
                premiumAmount: Number(formData.premiumAmount),
                coverageAmount: Number(formData.coverageAmount),
                duration: Number(formData.duration),

                customerName: formData.customerName,
                mobile: formData.mobile,
                email: formData.email,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                aadharNo: formData.aadharNo,
                panNo: formData.panNo,

                status: formData.status,
                updatedAt: serverTimestamp()
            };

            if (formData.id) {
                await updateDoc(doc(db, "insurance_customers", formData.id), payload);
            } else {
                await addDoc(collection(db, "insurance_customers"), {
                    ...payload,
                    createdAt: serverTimestamp()
                });
            }

            setOpen(false);
            fetchData();
            resetForm();

            Swal.fire("Success", "Saved successfully", "success");

        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    const showError = (msg) => {
        setOpen(false); // close dialog first

        setTimeout(() => {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: msg,
                confirmButtonColor: "#d33",
            });
        }, 200);
    };

    const resetForm = () => {
        setFormData({
            id: null,
            insuranceId: "",
            category: "",
            planName: "",
            premiumAmount: "",
            coverageAmount: "",
            duration: "",
            customerName: "",
            mobile: "",
            email: "",
            address: "",
            city: "",
            state: "",
            pincode: "",
            aadharNo: "",
            panNo: "",

            status: "Active"
        });
    };

    // 🔹 DELETE
    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete?",
            showCancelButton: true
        });

        if (confirm.isConfirmed) {
            await deleteDoc(doc(db, "insurance_customers", id));
            fetchData();
        }
    };

    // 🔹 SEARCH
    const filtered = data.filter(d =>
        d.customerName?.toLowerCase().includes(search.toLowerCase()) ||
        d.mobile?.includes(search)
    );

    return (
        <Box p={2}>

            {/* HEADER */}
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h5">Insurance Customers</Typography>
                <Button variant="contained" startIcon={<Plus />} onClick={() => setOpen(true)}>
                    Add Customer
                </Button>
            </Box>

            {/* SEARCH */}
            <TextField
                fullWidth
                size="small"
                placeholder="Search name / mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search size={16} />
                        </InputAdornment>
                    )
                }}
                sx={{ mb: 2 }}
            />

            {/* TABLE */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Mobile</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Premium</TableCell>
                                <TableCell>Coverage</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, i) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                                        <TableCell>{row.customerName}</TableCell>
                                        <TableCell>{row.mobile}</TableCell>
                                        <TableCell>{row.planName}</TableCell>
                                        <TableCell>₹{row.premiumAmount}</TableCell>
                                        <TableCell>₹{row.coverageAmount}</TableCell>
                                        <TableCell>{row.status}</TableCell>
                                        <TableCell>
                                            {row.createdAt?.seconds
                                                ? new Date(row.createdAt.seconds * 1000).toLocaleDateString()
                                                : "-"}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => { setFormData(row); setOpen(true); }}>
                                                <Edit3 size={16} />
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(row.id)}>
                                                <Trash2 size={16} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    count={filtered.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* MODAL */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{formData.id ? "Edit Customer" : "Add Customer"}</DialogTitle>

                <DialogContent>
                    <Grid className="row" spacing={2} mt={1}>

                        {/* Customer Info */}
                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Customer Name" size="small" fullWidth
                                value={formData.customerName}
                                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Mobile" size="small" fullWidth
                                value={formData.mobile}
                                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Email" size="small" fullWidth
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="PAN No" size="small" fullWidth
                                value={formData.panNo}
                                onChange={(e) => setFormData({ ...formData, panNo: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Aadhar No" size="small" fullWidth
                                value={formData.aadharNo}
                                onChange={(e) => setFormData({ ...formData, aadharNo: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12}>
                            <TextField label="Address" size="small" fullWidth
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="City" size="small" fullWidth
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="State" size="small" fullWidth
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={4}>
                            <TextField label="Pincode" size="small" fullWidth
                                value={formData.pincode}
                                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12}>
                            <TextField
                                select
                                size="small"
                                label="Select Insurance Plan"
                                fullWidth
                                value={formData.insuranceId}
                                onChange={(e) => {
                                    const plan = plans.find(p => p.id === e.target.value);
                                    if (plan) {
                                        setFormData({
                                            ...formData,
                                            insuranceId: plan.id,
                                            category: plan.category,
                                            planName: plan.planName,
                                            premiumAmount: plan.premiumAmount,
                                            coverageAmount: plan.coverageAmount,
                                            duration: plan.duration
                                        });
                                    }
                                }}
                                SelectProps={{ native: true }}
                            >
                                <option value="">Select Plan</option>
                                {plans.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.planName} - ₹{p.premiumAmount}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={6}>
                            <TextField label="Premium" size="small" fullWidth value={formData.premiumAmount} />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={6}>
                            <TextField label="Coverage" size="small" fullWidth value={formData.coverageAmount} />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12}>
                            <TextField
                                select
                                label="Status"
                                size="small"
                                fullWidth
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                SelectProps={{ native: true }}
                            >
                                <option value="Active">Active</option>
                                <option value="Closed">Closed</option>
                                <option value="Pending">Pending</option>
                            </TextField>
                        </Grid>

                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>Save</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Insurancecustomers;